/**
 * Cliente WebSocket con reconexión y salas.
 *
 * Una sola conexión para toda la aplicación. Los componentes se suscriben a
 * salas (`game/abc`, `tv/blitz`, …) y a tipos de evento; al reconectar, las
 * suscripciones se restablecen solas, de modo que una caída de red no deja
 * la partida muda.
 */

type Listener = (data: unknown, room?: string) => void;

interface Outgoing {
  t: string;
  room?: string;
  d?: unknown;
}

const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 15_000;
const PING_INTERVAL_MS = 25_000;

class SocketClient {
  private socket: WebSocket | null = null;
  private listeners = new Map<string, Set<Listener>>();
  private rooms = new Map<string, number>();
  private queue: Outgoing[] = [];
  private attempts = 0;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closedByUs = false;

  /** `connecting` | `open` | `closed` — para pintar el aviso de reconexión. */
  status: 'connecting' | 'open' | 'closed' = 'closed';

  connect(): void {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.closedByUs = false;
    this.status = 'connecting';
    this.emitLocal('status', this.status);

    const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
    const socket = new WebSocket(`${protocol}://${location.host}/ws`);
    this.socket = socket;

    // Tras reabrir el socket (al iniciar sesión), los sucesos del anterior
    // llegan tarde: no deben pisar el estado ni los temporizadores del nuevo.
    const current = () => this.socket === socket;

    socket.onopen = () => {
      if (!current()) return;
      this.attempts = 0;
      this.status = 'open';
      this.emitLocal('status', this.status);

      // Se recuperan las salas activas antes de vaciar la cola pendiente.
      for (const room of this.rooms.keys()) {
        socket.send(JSON.stringify({ t: 'sub', room }));
      }
      for (const message of this.queue.splice(0)) {
        socket.send(JSON.stringify(message));
      }

      this.pingTimer = setInterval(() => this.send('ping'), PING_INTERVAL_MS);
    };

    socket.onmessage = (event) => {
      if (!current()) return;
      let message: { t: string; room?: string; d?: unknown };
      try {
        message = JSON.parse(event.data as string);
      } catch {
        return;
      }
      if (!message?.t) return;
      this.dispatch(message.t, message.d, message.room);
    };

    socket.onclose = () => {
      if (!current()) return;
      this.cleanupTimers();
      this.status = 'closed';
      this.emitLocal('status', this.status);
      if (!this.closedByUs) this.scheduleReconnect();
    };

    socket.onerror = () => {
      // `onclose` llega justo después y se encarga de reintentar.
    };
  }

  disconnect(): void {
    this.closedByUs = true;
    this.cleanupTimers();
    this.socket?.close();
    this.socket = null;
    this.status = 'closed';
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    // Espera creciente con algo de aleatoriedad, para no reconectar todos
    // los clientes a la vez tras una caída del servidor.
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** this.attempts, RECONNECT_MAX_MS);
    const jitter = delay * 0.3 * Math.random();
    this.attempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay + jitter);
  }

  private cleanupTimers(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  /* ---------------------------------------------------------------- */
  /* Envío                                                             */
  /* ---------------------------------------------------------------- */

  send(type: string, data?: unknown, room?: string): void {
    const message: Outgoing = { t: type, ...(room ? { room } : {}), ...(data !== undefined ? { d: data } : {}) };
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      // No se pierde: se reenvía en cuanto vuelva la conexión.
      if (this.queue.length < 100) this.queue.push(message);
      this.connect();
    }
  }

  /* ---------------------------------------------------------------- */
  /* Salas                                                             */
  /* ---------------------------------------------------------------- */

  /**
   * Entra en una sala y devuelve la función para salir.
   * Lleva un contador por sala: varios componentes pueden escuchar la misma
   * partida y solo se abandona cuando se va el último.
   */
  join(room: string): () => void {
    const current = this.rooms.get(room) ?? 0;
    this.rooms.set(room, current + 1);
    if (current === 0) this.send('sub', undefined, room);

    let released = false;
    return () => {
      if (released) return;
      released = true;
      const next = (this.rooms.get(room) ?? 1) - 1;
      if (next <= 0) {
        this.rooms.delete(room);
        this.send('unsub', undefined, room);
      } else {
        this.rooms.set(room, next);
      }
    };
  }

  /* ---------------------------------------------------------------- */
  /* Eventos                                                           */
  /* ---------------------------------------------------------------- */

  /** Escucha un tipo de evento. Devuelve la función para dejar de escuchar. */
  on(type: string, listener: Listener): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(listener);

    return () => {
      set!.delete(listener);
      if (set!.size === 0) this.listeners.delete(type);
    };
  }

  private dispatch(type: string, data: unknown, room?: string): void {
    const set = this.listeners.get(type);
    if (!set) return;
    for (const listener of [...set]) {
      try {
        listener(data, room);
      } catch (error) {
        console.error('[ws] fallo en el manejador de', type, error);
      }
    }
  }

  private emitLocal(type: string, data: unknown): void {
    this.dispatch(type, data);
  }
}

export const socket = new SocketClient();
